import { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import { HourglassEmpty, Send, Cancel, ErrorOutline } from '@mui/icons-material'
import { formatDistanceToNow, isPast } from 'date-fns'

import { useBranding } from '../../hooks/useBranding'
import { mutations } from '../../graphql'
import { NotificationDialog } from '../dialog/notification-dialog'

function getButtonLabel(resending, isExpired, isTrulyExpired) {
  if (resending) return 'Sending...'
  if (isTrulyExpired) return 'Reissue Invitation'
  if (isExpired) return 'Resend & Extend'
  return 'Resend Invitation'
}

export function PendingInviteTile({ invite, onResendSuccess, onCancelSuccess }) {
  const [resending, setResending] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const { accentColor, accentTextColor } = useBranding()

  const isExpired = isPast(new Date(invite.expiresAt))
  const isTrulyExpired = invite.statusLabel === 'expired'
  const clientName = invite.corporateName || `${invite.clientFirstName} ${invite.clientLastName}`

  const [resendEmail] = useMutation(mutations.fundClaims.resendAdvisorFundClaimEmail, {
    onCompleted: () => {
      setResending(false)
      onResendSuccess?.('Invitation email has been resent.')
    },
    onError: (error) => {
      setResending(false)
      console.error('Failed to resend email:', error)
    },
  })

  const [reissueEmail] = useMutation(mutations.fundClaims.reissueAdvisorFundClaimEmail, {
    onCompleted: () => {
      setResending(false)
      onResendSuccess?.('Invitation has been reissued.')
    },
    onError: (error) => {
      setResending(false)
      console.error('Failed to reissue invite:', error)
    },
  })

  const [cancelInvite] = useMutation(mutations.fundClaims.cancelAdvisorFundClaimInvite, {
    onCompleted: () => {
      setCanceling(false)
      onCancelSuccess?.('Invitation has been cancelled.')
    },
    onError: () => {
      setCanceling(false)
    },
  })

  const handleResend = async () => {
    setResending(true)
    if (isTrulyExpired) {
      await reissueEmail({ variables: { fundClaimInviteId: invite.id } })
    } else {
      await resendEmail({ variables: { fundClaimInviteId: invite.id } })
    }
  }

  const handleCancel = () => {
    setConfirmCancelOpen(true)
  }

  const handleConfirmCancel = async () => {
    setConfirmCancelOpen(false)
    setCanceling(true)
    await cancelInvite({ variables: { fundClaimInviteId: invite.id } })
  }

  return (
    <Card
      sx={{
        height: '100%',
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        opacity: 0.9,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Client Name */}
        <Typography variant="h6" component="div" gutterBottom noWrap title={clientName}>
          {clientName}
        </Typography>

        {/* Fund Name */}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {invite.fundName || `${clientName} Giving Fund`}
        </Typography>

        {/* Status Badges */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
          <Tooltip
            title={`Invitation sent ${
              invite.sentAt ? formatDistanceToNow(new Date(invite.sentAt), { addSuffix: true }) : 'not yet'
            }`}
          >
            {isTrulyExpired ? (
              <Chip label="Invite Expired" size="small" color="error" icon={<ErrorOutline fontSize="small" />} />
            ) : (
              <Chip
                label="Pending Donor Approval"
                size="small"
                icon={<HourglassEmpty fontSize="small" />}
                sx={{ backgroundColor: accentColor, color: accentTextColor }}
              />
            )}
          </Tooltip>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Email:</strong> {invite.clientEmail}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Type:</strong> {invite.fundType === 'corporate' ? 'Corporate' : 'Personal'}
          </Typography>
          {invite.corporateName && (
            <Typography variant="body2" color="text.secondary">
              <strong>Organization:</strong> {invite.corporateName}
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleResend}
          disabled={resending || canceling}
          startIcon={resending ? <CircularProgress size={16} color="inherit" /> : <Send />}
        >
          {getButtonLabel(resending, isExpired, isTrulyExpired)}
        </Button>
        <Tooltip title="Cancel invitation">
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            disabled={resending || canceling}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {canceling ? <CircularProgress size={20} /> : <Cancel />}
          </Button>
        </Tooltip>
      </CardActions>

      <NotificationDialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        type="alert"
        title="Cancel Invitation"
        message="Are you sure you want to cancel this invitation? This action cannot be undone."
        actions={[
          { label: 'No, Keep It', onClick: () => setConfirmCancelOpen(false) },
          { label: 'Yes, Cancel', onClick: handleConfirmCancel, variant: 'contained', color: 'error' },
        ]}
      />
    </Card>
  )
}
